import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
} from '@unite-us/app-components';
import useUpdateNote from 'src/actions/Case/Contact/Group/useUpdateNote';
import Notifier from 'common/helpers/Notifier';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import 'src/common/form/Interactions/stylesheets/interactions.scss';
import EditGeneralNoteForm from './EditGeneralNoteForm';
import EditInteractionNoteForm from './EditInteractionNoteForm';
import EditServiceProvidedNoteForm from './EditServiceProvidedNoteForm';

const FORM_COMPONENTS = {
  general: EditGeneralNoteForm,
  interaction: EditInteractionNoteForm,
  service_provided: EditServiceProvidedNoteForm,
};

const INTERACTION_TYPE_BY_API_VALUE = {
  phone: 'phone_call',
  email: 'email',
  meeting: 'meeting',
};

const API_INTERACTION_TYPE_BY_FORM_VALUE = {
  phone_call: 'phone',
  email: 'email',
  meeting: 'meeting',
};

const toUnixDateString = (value) => {
  if (moment.isMoment(value)) {
    return value.clone().utc().startOf('day').unix().toString();
  }

  if (value instanceof Date) {
    return moment.utc(value).startOf('day').unix().toString();
  }

  const stringValue = String(value);

  return /^\d+$/.test(stringValue) ? stringValue : (
    moment.utc(stringValue, ['YYYY-MM-DD', 'MM/DD/YYYY'], true).startOf('day').unix().toString()
  );
};

const toApiDateString = (value) => {
  const unixDate = toUnixDateString(value);
  return unixDate ? moment.unix(Number(unixDate)).utc().format('YYYY-MM-DD') : undefined;
};

const buildGeneralInitialValues = (note, isPrivate) => ({
  attached_to: 'general',
  interaction: {
    type: 'note',
    note: note.text,
    is_note_private: isPrivate,
  },
});

const buildInteractionInitialValues = (note, isPrivate) => ({
  interaction: {
    attached_to: 'case',
    type: INTERACTION_TYPE_BY_API_VALUE[note.details?.interaction?.type] || 'phone_call',
    occurred_on: toUnixDateString(note.details?.interaction?.date),
    duration: note.details?.interaction?.duration?.toString(),
    note: note.text,
    is_note_private: isPrivate,
  },
  validateCases: false,
  caseOptions: [],
});

const buildServiceProvidedInitialValues = (note, isPrivate) => ({
  interaction: {
    is_note_private: isPrivate,
  },
  caseOptions: [],
  provided_service: {
    note: note.text,
    type: note.details?.service_provided?.type,
    amount: note.details?.service_provided?.amount,
    unit: note.details?.service_provided?.unit,
    date: toUnixDateString(note.details?.service_provided?.date),
  },
});

const INITIAL_VALUES_BY_CATEGORY = {
  general: buildGeneralInitialValues,
  interaction: buildInteractionInitialValues,
  service_provided: buildServiceProvidedInitialValues,
};

const buildInitialValues = (note) => {
  const isPrivate = note.visibility === 'private';
  const builder = INITIAL_VALUES_BY_CATEGORY[note.category] || buildGeneralInitialValues;
  return builder(note, isPrivate);
};

const buildGeneralUpdatePayload = (values) => ({
  text: values?.interaction?.note,
});

const buildInteractionUpdatePayload = (values) => {
  const { interaction = {} } = values;

  return {
    text: interaction.note,
    details: {
      interaction: {
        type: API_INTERACTION_TYPE_BY_FORM_VALUE[interaction.type] || 'phone',
        date: toUnixDateString(interaction.occurred_on),
        duration: interaction.type === 'email' ? null : parseInt(interaction.duration, 10),
      },
    },
  };
};

const buildServiceProvidedUpdatePayload = (values) => {
  const { provided_service = {} } = values;

  return {
    text: provided_service.note,
    details: {
      service_provided: {
        type: provided_service.type,
        amount: Number(provided_service.amount),
        unit: provided_service.unit,
        date: toApiDateString(provided_service.date),
      },
    },
  };
};

const UPDATE_PAYLOAD_BY_CATEGORY = {
  general: buildGeneralUpdatePayload,
  interaction: buildInteractionUpdatePayload,
  service_provided: buildServiceProvidedUpdatePayload,
};

const buildUpdatePayload = (note, values) => (
  (UPDATE_PAYLOAD_BY_CATEGORY[note.category] || buildGeneralUpdatePayload)(values)
);

const EditNoteForm = ({
  note,
  onCancel,
}) => {
  const FormComponent = FORM_COMPONENTS[note.category];
  const { updateRecord } = useUpdateNote();

  if (!FormComponent) {
    return null;
  }

  const handleSubmit = async (values) => {
    const response = await updateRecord(note.id, buildUpdatePayload(note, values));
    const status = response?.status;

    if (isHttpSuccess(status)) {
      Notifier.dispatch(status, 'Note updated');
      onCancel();
    }

    return response;
  };

  return (
    <div className="interactions new-activity" data-role="new-activity">
      <BaseCard>
        <BaseCardHeader title="Edit Note" />
        <BaseCardBody withPadding>
          <FormComponent
            allowDurationReset={note.category === 'interaction'}
            cancelFunction={onCancel}
            detailType={note.subject_type?.toLowerCase()}
            initialValues={buildInitialValues(note)}
            isFromCaseOrReferral
            key={note.id}
            submitFunction={handleSubmit}
          />
        </BaseCardBody>
      </BaseCard>
    </div>
  );
};

EditNoteForm.propTypes = {
  note: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default EditNoteForm;
